import { getRectOfNodes } from 'reactflow';
import { isNodeNotPositioned, snapPosition } from './graphLayoutPrimitives';
import { AUTO_POSITION } from './tokens';
import { NodeType } from './types';
/**
 * Partitions `nodes` into connected components using **edges** (both endpoints in `nodes`) and
 * **parent pointers**: each node with `parentNode` set is unioned with that id even when the parent
 * node is not in this batch. That ties siblings under the same container, chains nested subflows
 * (child → parent id → grandparent id), and keeps a freshly added subflow with its gems in one
 * component for ELK instead of splitting them across root-level singletons.
 */
export function partitionIntoConnectedComponents(nodes, edges) {
    if (nodes.length === 0)
        return [];
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    const nodeIdSet = new Set(nodes.map((node) => node.id));
    const parentOf = {};
    const findRoot = (id) => {
        if (parentOf[id] === undefined)
            parentOf[id] = id;
        if (parentOf[id] !== id)
            parentOf[id] = findRoot(parentOf[id]);
        return parentOf[id];
    };
    const merge = (idA, idB) => {
        const rootA = findRoot(idA);
        const rootB = findRoot(idB);
        if (rootA !== rootB)
            parentOf[rootB] = rootA;
    };
    nodes.forEach((node) => {
        parentOf[node.id] = node.id;
    });
    edges.forEach((edge) => {
        if (nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)) {
            merge(edge.source, edge.target);
        }
    });
    nodes.forEach((node) => {
        if (node.parentNode) {
            merge(node.id, node.parentNode);
        }
    });
    const componentsByRoot = new Map();
    nodes.forEach((node) => {
        const groupRoot = findRoot(node.id);
        const group = componentsByRoot.get(groupRoot) ?? [];
        group.push(nodesById.get(node.id));
        componentsByRoot.set(groupRoot, group);
    });
    return Array.from(componentsByRoot.values());
}
/**
 * Recompute `positionAbsolute` from `position` and `parentNode` chain. `nodeById` must contain every
 * ancestor (e.g. merged existing + new nodes). Used after applying a **canvas** offset to **root**
 * nodes only — children keep parent-relative `position` and must not receive the same delta.
 */
export function recomputeAbsolutePositions(nodes, nodeById = new Map(nodes.map((node) => [node.id, node]))) {
    const cache = new Map();
    const ancestors = new Set();
    const absoluteOf = (id) => {
        const cached = cache.get(id);
        if (cached)
            return cached;
        if (ancestors.has(id))
            return { x: 0, y: 0 };
        ancestors.add(id);
        const node = nodeById.get(id);
        if (!node) {
            ancestors.delete(id);
            return { x: 0, y: 0 };
        }
        let absolute;
        if (!node.parentNode || !nodeById.has(node.parentNode)) {
            absolute = { x: node.position.x, y: node.position.y };
        }
        else {
            const parentAbsolute = absoluteOf(node.parentNode);
            absolute = { x: parentAbsolute.x + node.position.x, y: parentAbsolute.y + node.position.y };
        }
        cache.set(id, absolute);
        ancestors.delete(id);
        return absolute;
    };
    nodes.forEach((node) => {
        node.positionAbsolute = absoluteOf(node.id);
    });
}
function isSubflowNode(node) {
    return node.type === NodeType.subFlow;
}
/**
 * Splits nodes into already-positioned vs unpositioned (new) sets for preserve-layout,
 * and records each positioned node's coordinates for later collision resolution.
 */
export function partitionNodesForPreserveLayout(nodes) {
    const existingNodes = [];
    const newNodes = [];
    const existingNodesPositions = new Map();
    nodes.forEach((node) => {
        if (isNodeNotPositioned(node) ||
            (isSubflowNode(node) && (node.data?.width ?? 0) >= AUTO_POSITION) ||
            (isSubflowNode(node) && (node.data?.height ?? 0) >= AUTO_POSITION)) {
            newNodes.push(node);
        }
        else {
            existingNodes.push(node);
            existingNodesPositions.set(node.id, node.position);
        }
    });
    return { existingNodes, newNodes, existingNodesPositions };
}
/**
 * Returns shallow copies with `position` set from `positionAbsolute ?? position` so callers can
 * treat `position` as canvas coordinates for collision and bounds math.
 */
export function getNodesCanvasPositions(nodes) {
    return nodes.map((n) => {
        const canvas = n.positionAbsolute ?? n.position;
        return {
            ...n,
            position: { x: canvas.x, y: canvas.y },
            positionAbsolute: n.positionAbsolute
                ? { x: n.positionAbsolute.x, y: n.positionAbsolute.y }
                : { x: canvas.x, y: canvas.y }
        };
    });
}
export function applyOffsetToSubgraphNodes(layoutedSubgraph, subgraphBounds, targetPosition) {
    const offsetX = targetPosition.x - subgraphBounds.x;
    const offsetY = targetPosition.y - subgraphBounds.y;
    layoutedSubgraph.forEach((node) => {
        node.position = snapPosition({
            x: node.position.x + offsetX,
            y: node.position.y + offsetY
        });
        node.positionAbsolute = { ...node.position };
    });
}
/** Add relative position to subflow children after a canvas offset on the subgraph. */
export function applyOffsetToSubflowChildren(layoutedSubgraph, existingNodeMap) {
    for (const node of layoutedSubgraph) {
        if (node.parentNode) {
            const parent = existingNodeMap.get(node.parentNode);
            if (parent) {
                node.positionAbsolute = { ...node.position };
                node.position = snapPosition({
                    x: node.position.x - parent.position.x,
                    y: node.position.y - parent.position.y
                });
            }
        }
    }
}
/**
 * Calculates the absolute position of a node in the graph by recursively
 * accumulating the positions of all ancestor nodes.
 *
 * Since ELK layout provides positions relative to a node's parent container,
 * this function traverses up the parent hierarchy to compute the final
 * absolute coordinates in the graph's coordinate system.
 *
 * @param nodeId - The ID of the node to get the absolute position for
 * @param nodeElkMap - Map of node IDs to their ELK node data (including parent references)
 * @param nodePositionMap - Map of node IDs to their relative positions from ELK layout
 * @returns The absolute XY position of the node, or undefined if the node is not found
 */
export function getNodeAbsolutePosition(nodeId, nodeElkMap, nodePositionMap) {
    const node = nodeElkMap.get(nodeId);
    if (!node) {
        return undefined;
    }
    const nodePosition = nodePositionMap.get(nodeId);
    if (!nodePosition) {
        return undefined;
    }
    let nodeX = nodePosition.x;
    let nodeY = nodePosition.y;
    if (node.parentId) {
        const nodeParentPosition = getNodeAbsolutePosition(node.parentId, nodeElkMap, nodePositionMap);
        if (nodeParentPosition) {
            nodeX += nodeParentPosition.x;
            nodeY += nodeParentPosition.y;
        }
    }
    return { x: nodeX, y: nodeY };
}
export function buildNodeIdToGraphNodeMap(node, nodeIdToGraphNodeMap = new Map()) {
    if (!node.children) {
        return nodeIdToGraphNodeMap;
    }
    node.children.forEach((child) => {
        const nodeValue = { ...child, parentId: node.id };
        nodeIdToGraphNodeMap.set(child.id, nodeValue);
        if (child.children) {
            buildNodeIdToGraphNodeMap(child, nodeIdToGraphNodeMap);
        }
    });
    return nodeIdToGraphNodeMap;
}
/**
 * Returns the bounding rect of all already-positioned nodes (existing + laid-out new).
 * Used to position completely disconnected subgraphs at the bottom of the canvas.
 */
export function getCurrentGraphBounds(adjustedExistingNodes, layoutedNewNodes) {
    if (adjustedExistingNodes.length === 0 && layoutedNewNodes.length === 0) {
        return { x: 0, y: 0, width: 0, height: 0 };
    }
    return getRectOfNodes([...adjustedExistingNodes, ...layoutedNewNodes]);
}
